/**
 * ImmersiveWorkoutCountdown - Pre-workout countdown with exercise preview
 */
import * as React from 'react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';
import { useTrans } from '../../../../Store/System/LocalizationStore';
import { useLanguage } from '../../../../Hooks/useLanguage';
import { useLocalized } from '../../../../Hooks/useLocalized';
import { ViewWorkout } from '../../../../Model/WorkoutLog/ViewWorkout';
import { useCountdownSeconds } from '../../../../Hooks/useCountdownSeconds';
import { CircularProgress } from './CircularProgress';
import { getMediaLargeOrMediumOrSmallest } from '../../../../Model/Media/Media';

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 300;
  padding-top: max(16px, env(safe-area-inset-top));
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
`;

const IconButton = styled.button<{ active?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background: ${({ active }) => (active ? `${Theme.Colors.primary}30` : 'rgba(255, 255, 255, 0.15)')};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  & > i {
    font-size: 24px;
    color: ${({ active }) => (active ? Theme.Colors.primary : '#fff')};
  }
`;

const CenterInfo = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const ModeBadge = styled.button<{ isAuto?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 16px;
  border: none;
  background: ${({ isAuto }) => (isAuto ? `${Theme.Colors.primary}30` : 'rgba(255, 255, 255, 0.2)')};
  color: ${({ isAuto }) => (isAuto ? Theme.Colors.primary : '#fff')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  & > i {
    font-size: 12px;
  }
`;

const WorkoutName = styled.div`
  margin-top: 32px;
  color: ${Theme.Colors.primary};
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-align: center;
`;

const CountdownContainer = styled.div`
  margin-top: 48px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 100px;
  padding: 10px;
`;

const CountdownNumber = styled.div`
  color: #fff;
  font-size: 72px;
  font-weight: 800;
  text-align: center;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 480px) {
    font-size: 56px;
  }
`;

const StartingText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  margin-top: 20px;
  font-size: 16px;
  font-weight: 600;
`;

const BottomSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
`;

const PreviewCard = styled.div`
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const PreviewLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const PreviewContent = styled.div`
  display: flex;
  align-items: center;
`;

const PreviewImage = styled.div<{ src?: string }>`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.1);
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const PreviewInfo = styled.div`
  flex: 1;
  margin-left: 16px;
`;

const PreviewExercise = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 18px;
`;

const PreviewDetails = styled.div`
  color: ${Theme.Colors.primary};
  margin-top: 4px;
  font-weight: 600;
  font-size: 13px;
`;

const StartButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 32px;
  border-radius: 32px;
  border: none;
  background: ${Theme.Colors.primary};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px ${Theme.Colors.primary}66;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${Theme.Colors.primary}88;
  }

  & > i {
    font-size: 24px;
  }
`;

export type ImmersiveWorkoutCountdownProps = {
  viewWorkout: ViewWorkout;
  isAutoMode: boolean;
  isAudioEnabled: boolean;
  isFullscreen?: boolean;
  onStart: () => void;
  onClose?: () => void;
  onToggleMode: () => void;
  onOpenSettings: () => void;
  onToggleFullscreen?: () => void;
};

export const ImmersiveWorkoutCountdown: React.FC<ImmersiveWorkoutCountdownProps> = observer(
  ({ viewWorkout, isAutoMode, isAudioEnabled, isFullscreen, onStart, onClose, onToggleMode, onOpenSettings, onToggleFullscreen }) => {
    const { t } = useTrans();
    const { language } = useLanguage();
    const workoutName = useLocalized(viewWorkout.workout?.name);

    const countdownDuration = 15;
    const workout = viewWorkout.workout;
    const firstViewBlock = viewWorkout.firstViewBlock;
    const firstSet = firstViewBlock?.firstSet;

    const exerciseName = firstSet?.exercise.localeName(language) ?? '';
    const exerciseMedia = firstViewBlock?.exerciseMedia;
    const exerciseImage = exerciseMedia ? getMediaLargeOrMediumOrSmallest(exerciseMedia) : undefined;

    const handleCountdownComplete = useCallback(() => {
      onStart();
    }, [onStart]);

    const { countdown } = useCountdownSeconds(countdownDuration, handleCountdownComplete);
    const countdownProgress = Math.max(0, Math.min(1, countdown / countdownDuration));

    return (
      <Container>
        <Header>
          <IconButton onClick={onClose}>
            <i className="mdi mdi-close" />
          </IconButton>

          <CenterInfo>
            <ModeBadge isAuto={isAutoMode} onClick={onToggleMode}>
              <i className={isAutoMode ? 'mdi mdi-timer-outline' : 'mdi mdi-dumbbell'} />
              {isAutoMode
                ? t('workout.mode.auto', { defaultValue: 'Auto' })
                : t('workout.mode.manual', { defaultValue: 'Manual' })}
            </ModeBadge>
          </CenterInfo>

          <IconButton onClick={onToggleFullscreen} style={{ marginRight: 8 }}>
            <i className={isFullscreen ? 'mdi mdi-fullscreen-exit' : 'mdi mdi-fullscreen'} />
          </IconButton>

          <IconButton active={isAudioEnabled} onClick={onOpenSettings}>
            <i className={isAudioEnabled ? 'mdi mdi-volume-high' : 'mdi mdi-volume-mute'} />
          </IconButton>
        </Header>

        <WorkoutName>{workoutName.toUpperCase()}</WorkoutName>

        <CountdownContainer>
          <CircularProgress size={180} strokeWidth={4} progress={countdownProgress}>
            <CountdownNumber>{Math.ceil(countdown)}</CountdownNumber>
          </CircularProgress>
        </CountdownContainer>

        <StartingText>{t('workout.startingIn', { defaultValue: 'Starting in' })}</StartingText>

        <BottomSection>
          <PreviewCard>
            <PreviewLabel>{t('workout.firstExercise', { defaultValue: 'FIRST EXERCISE' })}</PreviewLabel>
            <PreviewContent>
              <PreviewImage src={exerciseImage} />
              <PreviewInfo>
                <PreviewExercise>{exerciseName}</PreviewExercise>
                {firstSet && (
                  <PreviewDetails>
                    {firstSet.durationBased
                      ? `${firstSet.plannedDuration}s`
                      : firstSet.plannedRepetitions
                        ? `${firstSet.plannedRepetitions} reps`
                        : ''}
                  </PreviewDetails>
                )}
              </PreviewInfo>
            </PreviewContent>
          </PreviewCard>

          <StartButton onClick={onStart}>
            {t('workout.startNow', { defaultValue: 'START NOW' })}
            <i className="mdi mdi-play" />
          </StartButton>
        </BottomSection>
      </Container>
    );
  },
);
